import { useMemo } from 'react'
import { CalendarFold, CircleDollarSign, Gauge } from 'lucide-react'

import { DistanceMetrics, RoutesPath } from 'src/types'
import { useLocales } from 'src/contexts'

import { useNavigation } from 'src/hooks/useNavigation'
import { useManageMaintenanceHistory } from 'src/hooks/useManageMaintenanceHistory'
import { useManageFuelHistory } from 'src/hooks/useManageFuelHistory'
import { useConvertCurrency } from 'src/hooks/useConvertCurrency'
import { useAppSelector } from 'src/hooks/useAppSelector'
import { timestampToDate } from 'src/helpers/timestampToDate'
import { formattedDateByLang } from 'src/helpers/formatDateByLang'

import { useMyCars } from 'src/pages/MyCars/useMyCars'
import { convertDistance } from 'src/pages/Home/helper'
import { PeriodChartInMonth } from 'src/components/HomeCharBar/types'
import {
  getMappedData,
  mapCostHistory,
} from 'src/components/HomeCharBar/helpers'

export const useHome = () => {
  const { userCars } = useMyCars()
  const { allFuelHistory } = useManageFuelHistory()
  const { convertCurrency } = useConvertCurrency()
  const { navigateTo } = useNavigation()
  const { translations, locale: language } = useLocales()
  const { currency, distanceMetric } = useAppSelector(state => state.user)

  const activeCar = userCars?.find(car => car.isActive)
  const displayDistance = convertDistance(
    activeCar?.mileage || 0,
    activeCar?.distanceMetric || DistanceMetrics.KM,
    distanceMetric,
  )

  const { allMaintenanceHistory } = useManageMaintenanceHistory()

  const periodMonth = PeriodChartInMonth.MONTH

  const periodStartDate = useMemo(() => {
    const now = new Date()

    return new Date(now.getFullYear(), now.getMonth(), 1)
  }, [periodMonth])

  const periodEndDate = useMemo(() => {
    const now = new Date()

    return new Date(now.getFullYear(), now.getMonth() + 1, 1)
  }, [periodMonth])

  const filteredFuelHistory = useMemo(() => {
    return (
      allFuelHistory?.filter(item => {
        const date = timestampToDate(item.dateOfRefuel)

        return date >= periodStartDate && date < periodEndDate
      }) || []
    )
  }, [allFuelHistory, periodStartDate, periodEndDate])

  const filteredMaintenanceHistory = useMemo(() => {
    return (
      allMaintenanceHistory?.filter(item => {
        const date = timestampToDate(item.date)

        return date >= periodStartDate && date < periodEndDate
      }) || []
    )
  }, [allMaintenanceHistory, periodStartDate, periodEndDate])

  const total = useMemo(() => {
    const mapFuelHistory = mapCostHistory(
      'totalCost',
      'dateOfRefuel',
      'currency',
      filteredFuelHistory,
    )
    const mapMaintenanceHistory = mapCostHistory(
      'serviceCost',
      'date',
      'currency',
      filteredMaintenanceHistory,
    )

    const mappedData = getMappedData(
      mapFuelHistory,
      mapMaintenanceHistory,
      currency,
      convertCurrency,
    )

    return Number(
      mappedData
        .reduce((acc, curr) => acc + curr.maintenance + curr.fuel, 0)
        .toFixed(2),
    )
  }, [
    filteredFuelHistory,
    filteredMaintenanceHistory,
    currency,
    convertCurrency,
  ])

  const lastFuel = useMemo(() => {
    return Array.isArray(allFuelHistory) && allFuelHistory.length
      ? allFuelHistory.sort(
          (a, b) => b.dateOfRefuel.seconds - a.dateOfRefuel.seconds,
        )[0]
      : null
  }, [allFuelHistory?.length])

  const dashboardCards = [
    {
      title: translations.homePage.dashboards.Millage,
      icon: Gauge,
      value: `${displayDistance || 'N/A'} ${distanceMetric}`,
      route: (): void => navigateTo(RoutesPath.MY_CARS),
      id: 1,
    },
    {
      title: translations.homePage.dashboards.SpendPerMonth,
      icon: CircleDollarSign,
      value: `${total} ${currency.toUpperCase()}` || 'N/A',
      route: null,
      id: 2,
    },
    {
      title: translations.homePage.dashboards.LastRefueling,
      icon: CalendarFold,
      value:
        formattedDateByLang(lastFuel?.dateOfRefuel?.seconds || 0, language) ||
        'N/A',
      route: (): void => navigateTo(RoutesPath.FUEL_HISTORY),
      id: 3,
    },
    {
      title: translations.homePage.dashboards.LastMaintenance,
      icon: CalendarFold,
      value:
        formattedDateByLang(
          activeCar?.lastServiceDate?.seconds || 0,
          language,
        ) || 'N/A',
      route: (): void => navigateTo(RoutesPath.SERVICE_MAINTENANCE_HISTORY),
      id: 4,
    },
  ]

  return { translations, dashboardCards }
}
